import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useRef, useMemo } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft, FiUpload, FiX } from 'react-icons/fi';
import { Card, Button, Input } from '@/components';
import { apiJson, ApiError, formatApiErrorForDisplay } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import {
  isValidCurrencyInput,
  dollarsToCents,
  formatDateLocal,
} from '@/lib/utils';
import type {
  ExpenseCategory,
  ExpenseCategoryListResponse,
  UserResponse,
  HouseholdResponse,
  PresignedUrlResponse,
} from '@/lib/types';
import {
  buildExpenseRrule,
  EXPENSE_RECURRENCE_OPTIONS,
  type ExpenseRecurrencePreset,
  previewExpenseOccurrences,
} from '@/lib/expense-recurrence';
import { buildExpenseSplits } from '@/lib/expense-settlement';

type ParseReceiptResponse = {
  name: string | null;
  amount_dollars: number | null;
  category_id: string | null;
  date: string | null;
};

function imageFileToJpegBase64(file: File, maxEdge = 1024): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        const scale = Math.min(1, maxEdge / Math.max(w, h));
        w = Math.round(w * scale);
        h = Math.round(h * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        const base64 = dataUrl.split(',')[1];
        if (!base64) reject(new Error('No base64'));
        else resolve(base64);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image load failed'));
    };
    img.src = url;
  });
}

function RecordExpensePage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track screen view
  useEffect(() => {
    track('Record Expense Screen');
  }, []);

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(() => formatDateLocal(new Date()));
  const [recurrence, setRecurrence] = useState<ExpenseRecurrencePreset>('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | null>(null);
  const [note, setNote] = useState('');
  const [paidBy, setPaidBy] = useState<'you' | 'coparent'>('you');
  const [splitPercent, setSplitPercent] = useState(50);
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<number[]>([]);
  const [isGeneral, setIsGeneral] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isParsingReceipt, setIsParsingReceipt] = useState(false);
  const [error, setError] = useState('');
  const [createdExpenseId, setCreatedExpenseId] = useState<number | null>(null);
  const [failedAttachmentFiles, setFailedAttachmentFiles] = useState<File[]>([]);

  // Fetch user
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  // Fetch households
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  // Fetch categories
  const categoriesQuery = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => apiJson<ExpenseCategoryListResponse>(getToken, '/v1/expense-categories'),
  });

  const categories = useMemo(() => categoriesQuery.data?.categories ?? [], [categoriesQuery.data?.categories]);
  const purchaseDateObj = useMemo(() => new Date(purchaseDate), [purchaseDate]);
  const recurrenceEndObj = useMemo(
    () => (recurrenceEndDate ? new Date(recurrenceEndDate) : null),
    [recurrenceEndDate]
  );
  const recurrenceRule = useMemo(
    () => buildExpenseRrule(recurrence, purchaseDateObj, recurrenceEndObj),
    [recurrence, purchaseDateObj, recurrenceEndObj]
  );
  const previewOccurrences = useMemo(
    () => previewExpenseOccurrences(recurrenceRule, purchaseDateObj),
    [recurrenceRule, purchaseDateObj]
  );
  const currentUser = userQuery.data;
  const { currentHouseholdId } = useHouseholds();
  const households = householdsQuery.data ?? [];
  const firstHousehold =
    households.find((h) => h.id === currentHouseholdId) ?? households[0];
  const beneficiaries = firstHousehold?.beneficiaries ?? [];
  const members = firstHousehold?.members ?? [];
  const coparent = members.find((m) => m.user_id !== currentUser?.id);
  const coparentName = coparent?.first_name ?? 'Co-parent';

  // Set default category
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      const general = categories.find((c) => c.name === 'General');
      setSelectedCategory(general ?? categories[0]);
    }
  }, [categories, selectedCategory]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || isValidCurrencyInput(value)) {
      setAmount(value);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (selectedFiles.length + files.length > 3) {
      setError('You can attach up to 3 images.');
      e.target.value = '';
      return;
    }
    const toAdd = files.slice(0, 3 - selectedFiles.length);
    setSelectedFiles([...selectedFiles, ...toAdd]);
    e.target.value = '';

    const imgFile = toAdd.find((f) => f.type.startsWith('image/'));
    const cats = categoriesQuery.data?.categories;
    if (!imgFile || !cats?.length) return;

    void (async () => {
      setIsParsingReceipt(true);
      try {
        const image_base64 = await imageFileToJpegBase64(imgFile);
        const parsed = await apiJson<ParseReceiptResponse>(getToken, '/v1/receipts/parse', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_base64,
            content_type: 'image/jpeg',
          }),
        });

        const fieldsParsed: string[] = [];
        if (parsed.name) {
          setName(parsed.name);
          fieldsParsed.push('name');
        }
        if (parsed.amount_dollars != null) {
          setAmount(parsed.amount_dollars.toFixed(2));
          fieldsParsed.push('amount');
        }
        if (parsed.category_id) {
          const matched = cats.find(
            (c) => String(c.expense_category_id) === String(parsed.category_id)
          );
          if (matched) {
            setSelectedCategory(matched);
            fieldsParsed.push('category');
          }
        }
        if (parsed.date) {
          const [year, month, day] = parsed.date.split('-').map(Number);
          if (year && month && day) {
            const parsedDate = new Date(year, month - 1, day);
            if (!isNaN(parsedDate.getTime()) && parsedDate <= new Date()) {
              setPurchaseDate(formatDateLocal(parsedDate));
              fieldsParsed.push('date');
            }
          }
        }
        track('Receipt Parse Completed', {
          fields_parsed: fieldsParsed,
          fields_parsed_count: fieldsParsed.length,
        });
      } catch {
        track('Receipt Parse Failed');
      } finally {
        setIsParsingReceipt(false);
      }
    })();
  };

  const removeFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const toggleBeneficiary = (id: number) => {
    if (selectedBeneficiaries.includes(id)) {
      setSelectedBeneficiaries(selectedBeneficiaries.filter((b) => b !== id));
    } else {
      setIsGeneral(false);
      setSelectedBeneficiaries([...selectedBeneficiaries, id]);
    }
  };

  const toggleGeneral = () => {
    if (isGeneral) {
      setIsGeneral(false);
    } else {
      setIsGeneral(true);
      setSelectedBeneficiaries([]);
    }
  };

  // Upload file to presigned URL
  const buildAttachmentFileName = (file: File, index: number): string => {
    const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    return `attachment-${Date.now()}-${index}.${extension}`;
  };

  const uploadFileToPresignedUrl = async (
    file: File,
    presignedUrl: string
  ): Promise<void> => {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });

    if (!response.ok) {
      const responseText = await response.text().catch(() => '');
      throw new Error(
        `Upload failed (${response.status}): ${
          responseText.substring(0, 200) || response.statusText
        }`
      );
    }
  };

  const uploadExpenseAttachment = async (
    expenseId: number,
    file: File,
    index: number
  ): Promise<void> => {
    const fileName = buildAttachmentFileName(file, index);
    const presignedResponse = await apiJson<PresignedUrlResponse>(
      getToken,
      `/v1/expenses/${expenseId}/attachments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fileName,
          attachment_type: file.type || 'application/octet-stream',
        }),
      }
    );

    await uploadFileToPresignedUrl(file, presignedResponse.presigned_url);
  };

  const uploadAttachments = async (
    expenseId: number,
    files: File[]
  ): Promise<File[]> => {
    const failedFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      try {
        await uploadExpenseAttachment(expenseId, files[i], i);
      } catch (uploadError) {
        console.error(`Failed to upload attachment ${i + 1}:`, uploadError);
        failedFiles.push(files[i]);
      }
    }

    return failedFiles;
  };

  const mutation = useMutation({
    mutationFn: async () => {
      setError('');

      // Retry only failed attachments for an expense that was already created.
      if (createdExpenseId && failedAttachmentFiles.length > 0) {
        const failedFiles = await uploadAttachments(
          createdExpenseId,
          failedAttachmentFiles
        );

        if (failedFiles.length > 0) {
          setFailedAttachmentFiles(failedFiles);
          throw new Error(
            failedFiles.length === failedAttachmentFiles.length
              ? `Failed to upload ${failedFiles.length} attachment(s). Please try again.`
              : `Expense saved, but ${failedFiles.length} attachment(s) failed to upload. Tap "Retry attachments" to try again.`
          );
        }

        setFailedAttachmentFiles([]);
        return { id: createdExpenseId };
      }

      if (!name.trim()) throw new Error('Please enter a name.');
      const amountCents = dollarsToCents(amount);
      if (!amountCents) throw new Error('Please enter a valid amount.');
      if (!selectedCategory) throw new Error('Please select a category.');
      if (!firstHousehold) throw new Error('No household found.');
      if (!isGeneral && selectedBeneficiaries.length === 0)
        throw new Error('Please select who this expense is for, or choose "General".');
      if (!currentUser) throw new Error('User not ready.');

      // Determine purchaser
      let purchaser_user_id: number | null;
      if (paidBy === 'you') {
        purchaser_user_id = currentUser.id;
      } else {
        purchaser_user_id = coparent?.user_id ?? null;
      }

      const splits = buildExpenseSplits({
        amountCents,
        currentUserId: currentUser.id,
        coparentUserId: coparent?.user_id ?? null,
        myPercent: splitPercent,
      });

      const payload = {
        household_id: firstHousehold.id,
        purchaser_user_id,
        purchase_date: new Date(purchaseDate).toISOString(),
        name: name.trim(),
        amount_cents: amountCents,
        category: selectedCategory.expense_category_id,
        note: note.trim() || null,
        splits,
        beneficiary_ids: isGeneral ? [] : selectedBeneficiaries,
        rrule: recurrenceRule,
      };

      const expense = await apiJson<{ id: number }>(getToken, '/v1/expenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      setCreatedExpenseId(expense.id);

      track('Expense Saved', {
        is_general: isGeneral,
        beneficiary_count: isGeneral ? 0 : selectedBeneficiaries.length,
        is_recurring: recurrence !== 'none',
        recurrence_preset: recurrence,
      });
      if (recurrence !== 'none') {
        track('Recurring Expense Created', {
          recurrence_preset: recurrence,
        });
      }

      if (selectedFiles.length > 0) {
        const failedFiles = await uploadAttachments(expense.id, selectedFiles);

        if (failedFiles.length > 0) {
          setFailedAttachmentFiles(failedFiles);
          throw new Error(
            failedFiles.length === selectedFiles.length
              ? `Expense saved, but all ${failedFiles.length} attachment(s) failed to upload. Tap "Retry attachments" to try again.`
              : `Expense saved, but ${failedFiles.length} attachment(s) failed to upload. Tap "Retry attachments" to try again.`
          );
        }
      }

      return expense;
    },
    onSuccess: async () => {
      setCreatedExpenseId(null);
      setFailedAttachmentFiles([]);
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      await queryClient.invalidateQueries({ queryKey: ['accounting', 'summary'] });
      navigate({ to: '/app/expenses' });
    },
    onError: (err: unknown) => {
      setError(formatApiErrorForDisplay(err));
    },
  });

  const isFormValid =
    name.trim() &&
    dollarsToCents(amount) &&
    selectedCategory &&
    (isGeneral || selectedBeneficiaries.length > 0);
  const isRetryingAttachments =
    createdExpenseId !== null && failedAttachmentFiles.length > 0;
  const saveButtonLabel = mutation.isPending
    ? isRetryingAttachments
      ? 'Uploading attachments...'
      : 'Saving...'
    : isRetryingAttachments
      ? 'Retry attachments'
      : 'Save expense';

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Mobile Header - hidden on desktop */}
      <header className='lg:hidden bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4'>
          <Link
            to='/app/expenses'
            className='inline-flex items-center gap-2 text-primary hover:underline'
          >
            <FiArrowLeft /> Back
          </Link>
        </div>
      </header>

      {/* Desktop Header - shown only on large screens */}
      <header className='hidden lg:block bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-2xl mx-auto px-8 py-4'>
          <h1
            className='text-xl font-semibold text-gray-900'
            style={{
              fontFamily: "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            New Expense
          </h1>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-6'>
        <p className='text-gray-500 text-center lg:text-left mb-6'>Record an expense for your household.</p>

        <Card className='space-y-5'>
          {/* Name */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Piano lessons'
              disabled={mutation.isPending}
            />
          </div>

          {/* Amount */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Amount</label>
            <Input
              value={amount}
              onChange={handleAmountChange}
              placeholder='$120.00'
              disabled={mutation.isPending}
            />
          </div>

          {/* Date */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Date</label>
            <input
              type='date'
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              max={formatDateLocal(new Date())}
              disabled={mutation.isPending}
              className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
            />
          </div>

          {/* Repeat */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Repeat</label>
            <div className='flex flex-wrap gap-2'>
              {EXPENSE_RECURRENCE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type='button'
                  disabled={mutation.isPending}
                  onClick={() => {
                    setRecurrence(option.value);
                    if (option.value === 'none') setRecurrenceEndDate('');
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${
                    recurrence === option.value
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {recurrence !== 'none' && (
              <div className='mt-3 space-y-2'>
                <label className='block text-sm font-semibold text-gray-600'>
                  Ends on (optional)
                </label>
                <input
                  type='date'
                  value={recurrenceEndDate}
                  min={purchaseDate}
                  onChange={(e) => setRecurrenceEndDate(e.target.value)}
                  disabled={mutation.isPending}
                  className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                />
                {previewOccurrences.length > 0 && (
                  <div className='rounded-xl bg-gray-50 p-3 text-sm text-gray-600'>
                    <div className='font-semibold text-gray-800 mb-1'>Next dates</div>
                    {previewOccurrences.map((d) => (
                      <div key={d}>{d}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Category */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Category</label>
            <select
              value={selectedCategory?.expense_category_id ?? ''}
              onChange={(e) => {
                const cat = categories.find((c) => c.expense_category_id === e.target.value);
                setSelectedCategory(cat ?? null);
              }}
              disabled={mutation.isPending || categoriesQuery.isLoading}
              className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
            >
              {categories.map((cat) => (
                <option key={cat.expense_category_id} value={cat.expense_category_id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Note */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Note (optional)</label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='Any details...'
              disabled={mutation.isPending}
            />
          </div>

          {/* Attachments */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>
              Attachments (optional)
            </label>
            {isParsingReceipt && (
              <p className='text-sm text-primary font-semibold mb-2'>Scanning receipt…</p>
            )}
            <div className='flex flex-wrap gap-3'>
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className='relative w-24 h-24 rounded-xl overflow-hidden border border-gray-200'
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className='w-full h-full object-cover'
                  />
                  <button
                    onClick={() => removeFile(index)}
                    disabled={mutation.isPending}
                    className='absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors'
                  >
                    <FiX className='w-3 h-3' />
                  </button>
                </div>
              ))}
              {selectedFiles.length < 3 && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={mutation.isPending || isParsingReceipt}
                  className='w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center text-primary hover:border-primary hover:bg-primary/5 transition-colors'
                >
                  <FiUpload className='w-5 h-5 mb-1' />
                  <span className='text-xs font-semibold'>Add Photo</span>
                </button>
              )}
              <input
                ref={fileInputRef}
                type='file'
                accept='image/*,application/pdf'
                onChange={handleFileSelect}
                className='hidden'
              />
            </div>
          </div>

          {/* Paid by */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>Paid by</label>
            <div className='flex rounded-2xl border border-gray-200 overflow-hidden'>
              <button
                onClick={() => setPaidBy('you')}
                disabled={mutation.isPending}
                className={`flex-1 py-3 text-center font-semibold transition-colors ${
                  paidBy === 'you'
                    ? 'bg-primary text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                You
              </button>
              <button
                onClick={() => setPaidBy('coparent')}
                disabled={mutation.isPending}
                className={`flex-1 py-3 text-center font-semibold transition-colors ${
                  paidBy === 'coparent'
                    ? 'bg-primary text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {coparentName}
              </button>
            </div>
          </div>

          {/* Split */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>
              How it should be split
            </label>
            <div className='bg-gray-50 rounded-2xl border border-gray-200 p-4'>
              <div className='flex justify-between mb-2'>
                <div>
                  <p className='text-xs text-gray-500'>You</p>
                  <p className='text-lg font-bold text-gray-900'>{splitPercent}%</p>
                </div>
                <div className='text-right'>
                  <p className='text-xs text-gray-500'>{coparentName}</p>
                  <p className='text-lg font-bold text-gray-900'>{100 - splitPercent}%</p>
                </div>
              </div>
              <input
                type='range'
                min={0}
                max={100}
                value={splitPercent}
                onChange={(e) => setSplitPercent(Number(e.target.value))}
                disabled={mutation.isPending}
                className='w-full h-2 bg-primary/20 rounded-lg appearance-none cursor-pointer accent-primary'
              />
              <p className='text-xs text-gray-500 mt-3 mb-2'>My Split</p>
              <div className='flex gap-2'>
                {[0, 25, 50, 75, 100].map((preset) => (
                  <button
                    key={preset}
                    onClick={() => setSplitPercent(preset)}
                    disabled={mutation.isPending}
                    className={`flex-1 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                      splitPercent === preset
                        ? 'bg-primary text-white'
                        : 'bg-primary/10 text-primary hover:bg-primary/20'
                    }`}
                  >
                    {preset}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Beneficiaries */}
          <div>
            <label className='block text-sm font-semibold text-gray-600 mb-2'>For</label>
            <div className='space-y-2'>
              <button
                onClick={toggleGeneral}
                disabled={mutation.isPending}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                  isGeneral
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                    isGeneral ? 'border-primary bg-primary text-white' : 'border-gray-300'
                  }`}
                >
                  {isGeneral && <span className='text-sm'>✓</span>}
                </div>
                <div className='text-left'>
                  <p className='font-semibold text-gray-900'>General</p>
                  <p className='text-xs text-gray-500'>Not for a specific child</p>
                </div>
              </button>
              {beneficiaries.map((b) => (
                <button
                  key={b.beneficiary_id}
                  onClick={() => toggleBeneficiary(b.beneficiary_id)}
                  disabled={mutation.isPending}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    selectedBeneficiaries.includes(b.beneficiary_id)
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                  }`}
                >
                  <div
                    className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                      selectedBeneficiaries.includes(b.beneficiary_id)
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-300'
                    }`}
                  >
                    {selectedBeneficiaries.includes(b.beneficiary_id) && (
                      <span className='text-sm'>✓</span>
                    )}
                  </div>
                  <div className='text-left'>
                    <p className='font-semibold text-gray-900'>{b.name}</p>
                    <p className='text-xs text-gray-500'>
                      {b.type === 'CHILD' ? 'Child' : b.type === 'PET' ? 'Pet' : 'Other'}
                    </p>
                  </div>
                </button>
              ))}
              {beneficiaries.length === 0 && (
                <div className='bg-gray-50 rounded-2xl border border-gray-200 p-4 text-center'>
                  <p className='text-gray-500 text-sm mb-3'>
                    No beneficiaries added yet. Add one to track expenses for a specific child,
                    pet, or other.
                  </p>
                  <Link
                    to='/app/settings'
                    className='inline-block px-4 py-2 bg-primary text-white rounded-xl font-semibold hover:bg-primary-hover transition-colors'
                  >
                    Add Beneficiary
                  </Link>
                </div>
              )}
            </div>
          </div>

          {error && <p className='text-sm text-red-500 text-center'>{error}</p>}

          {isRetryingAttachments && (
            <p className='text-sm text-amber-600 text-center'>
              Your expense was saved.{' '}
              {failedAttachmentFiles.length === 1
                ? '1 attachment still needs to upload.'
                : `${failedAttachmentFiles.length} attachments still need to upload.`}
            </p>
          )}

          <Button
            fullWidth
            onClick={() => mutation.mutate()}
            disabled={
              mutation.isPending ||
              (!isFormValid && !isRetryingAttachments)
            }
            loading={mutation.isPending}
          >
            {saveButtonLabel}
          </Button>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/expenses/record')({
  component: RecordExpensePage,
});
